import React from 'react';
import { FiHome, FiMapPin, FiDollarSign, FiCalendar } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  TextArea,
  CheckboxGroup,
  CheckboxLabel,
  InfoBox,
  HelpText
} from './FormStyles';
import { HousingPreferences } from '@/types/application-forms';

interface HousingPreferencesSectionProps {
  data: Partial<HousingPreferences>;
  onChange: (data: Partial<HousingPreferences>) => void;
  readonly?: boolean;
}

export const HousingPreferencesSection: React.FC<HousingPreferencesSectionProps> = ({
  data,
  onChange,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleArrayToggle = (field: 'type_logement_souhaite' | 'criteres_importants', value: string) => {
    const currentArray = (data[field] || []) as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    handleChange(field, newArray);
  };

  const handleQuartiersChange = (value: string) => {
    const quartiers = value.split(',').map(q => q.trim()).filter(q => q);
    handleChange('quartiers_preferes', quartiers);
  };

  const handleCommunesChange = (value: string) => {
    const communes = value.split(',').map(c => c.trim()).filter(c => c);
    handleChange('communes_preferees', communes);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiHome />
        Préférences de Logement
      </SectionTitle>

      <InfoBox $type="info">
        <FiMapPin />
        <p>
          Indiquez vos préférences de logement. Ces informations nous aideront à vous proposer
          les logements les plus adaptés à vos besoins.
        </p>
      </InfoBox>

      <SubSectionTitle>Type de Logement</SubSectionTitle>

      <FormGroup>
        <Label $required>Type(s) de logement souhaité(s)</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.type_logement_souhaite || []).includes('F1')}
              onChange={() => handleArrayToggle('type_logement_souhaite', 'F1')}
              disabled={readonly}
            />
            F1 (Studio)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.type_logement_souhaite || []).includes('F2')}
              onChange={() => handleArrayToggle('type_logement_souhaite', 'F2')}
              disabled={readonly}
            />
            F2 (1 chambre)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.type_logement_souhaite || []).includes('F3')}
              onChange={() => handleArrayToggle('type_logement_souhaite', 'F3')}
              disabled={readonly}
            />
            F3 (2 chambres)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.type_logement_souhaite || []).includes('F4')}
              onChange={() => handleArrayToggle('type_logement_souhaite', 'F4')}
              disabled={readonly}
            />
            F4 (3 chambres)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.type_logement_souhaite || []).includes('F5+')}
              onChange={() => handleArrayToggle('type_logement_souhaite', 'F5+')}
              disabled={readonly}
            />
            F5+ (4+ chambres)
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs types</HelpText>
      </FormGroup>

      <SubSectionTitle>Localisation</SubSectionTitle>

      <FormGroup>
        <Label>Quartier(s) préféré(s)</Label>
        <Input
          type="text"
          value={(data.quartiers_preferes || []).join(', ')}
          onChange={e => handleQuartiersChange(e.target.value)}
          placeholder="Ex: Cocody, Marcory, Plateau"
          disabled={readonly}
        />
        <HelpText>Séparez les quartiers par des virgules</HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Commune(s) préférée(s)</Label>
        <Input
          type="text"
          value={(data.communes_preferees || []).join(', ')}
          onChange={e => handleCommunesChange(e.target.value)}
          placeholder="Ex: Abobo, Yopougon"
          disabled={readonly}
        />
        <HelpText>Séparez les communes par des virgules</HelpText>
      </FormGroup>

      <SubSectionTitle>Budget et Timing</SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label $required>Budget mensuel maximum (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.budget_mensuel_max || ''}
            onChange={e => handleChange('budget_mensuel_max', parseInt(e.target.value))}
            placeholder="Ex: 150000"
            disabled={readonly}
          />
          <HelpText>Montant maximum que vous pouvez payer par mois</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Date souhaitée d'emménagement</Label>
          <Input
            type="date"
            value={data.date_souhaitee_emmenagement || ''}
            onChange={e => handleChange('date_souhaitee_emmenagement', e.target.value)}
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <SubSectionTitle>Critères Importants</SubSectionTitle>

      <FormGroup>
        <Label>Qu'est-ce qui est important pour vous ?</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Proximité lieu de travail')}
              onChange={() => handleArrayToggle('criteres_importants', 'Proximité lieu de travail')}
              disabled={readonly}
            />
            Proximité du lieu de travail
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Proximité écoles')}
              onChange={() => handleArrayToggle('criteres_importants', 'Proximité écoles')}
              disabled={readonly}
            />
            Proximité des écoles
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Proximité transports')}
              onChange={() => handleArrayToggle('criteres_importants', 'Proximité transports')}
              disabled={readonly}
            />
            Proximité des transports
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Sécurité')}
              onChange={() => handleArrayToggle('criteres_importants', 'Sécurité')}
              disabled={readonly}
            />
            Sécurité du quartier
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Commerces de proximité')}
              onChange={() => handleArrayToggle('criteres_importants', 'Commerces de proximité')}
              disabled={readonly}
            />
            Commerces de proximité
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Stationnement')}
              onChange={() => handleArrayToggle('criteres_importants', 'Stationnement')}
              disabled={readonly}
            />
            Stationnement
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.criteres_importants || []).includes('Autre')}
              onChange={() => handleArrayToggle('criteres_importants', 'Autre')}
              disabled={readonly}
            />
            Autre
          </CheckboxLabel>
        </CheckboxGroup>
      </FormGroup>

      <FormGroup>
        <Label>Autres précisions</Label>
        <TextArea
          value={data.autres_precisions || ''}
          onChange={e => handleChange('autres_precisions', e.target.value)}
          placeholder="Ajoutez toute information qui pourrait nous aider à mieux comprendre vos besoins..."
          disabled={readonly}
        />
      </FormGroup>
    </FormSection>
  );
};
